import { ArrowRight, Calendar, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Hero = () => {
  const navigate = useNavigate();

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ background: 'var(--bg-dark)' }}>
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 z-0">
        {/* Main gradient */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(59, 130, 246, 0.3), transparent), radial-gradient(ellipse 60% 40% at 80% 60%, rgba(139, 92, 246, 0.2), transparent), radial-gradient(ellipse 50% 30% at 20% 80%, rgba(249, 115, 22, 0.15), transparent)'
          }}
        ></div>
        
        {/* Animated Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(255,255,255,0.1) 1px, rgba(255,255,255,0.1) 2px),
              repeating-linear-gradient(-90deg, transparent, transparent 1px, rgba(255,255,255,0.1) 1px, rgba(255,255,255,0.1) 2px)
            `,
            backgroundSize: '60px 60px'
          }}
        ></div>

        {/* Animated Orbs */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#3B82F6]/20 rounded-full blur-[150px] animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#8B5CF6]/15 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 right-1/3 w-[300px] h-[300px] bg-[#F97316]/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-[7.6923%] py-32 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full mb-8 animate-fade-in hover:border-[#3B82F6]/50 transition-colors">
            <Sparkles size={16} className="text-[#F97316]" />
            <span className="text-white/90 text-sm font-medium">Next-Generation Digital Solutions</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6 animate-fade-in-up" style={{ letterSpacing: '-0.03em' }}>
            Engineering the Future of{' '}
            <span className="bg-gradient-to-r from-[#3B82F6] via-[#8B5CF6] to-[#F97316] bg-clip-text text-transparent">
              Digital Intelligence
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-white/70 mb-12 leading-relaxed animate-fade-in-up max-w-3xl mx-auto" style={{ animationDelay: '0.2s' }}>
            We build powerful digital platforms, intelligent AI systems, and next-generation business tools that drive real results.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <button 
              data-testid="get-started-btn"
              onClick={() => navigate('/signup')}
              className="group relative px-8 py-4 text-lg font-semibold text-white rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#3B82F6]/25 w-full sm:w-auto"
              style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                Get Started
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </button>
            <button 
              data-testid="consultation-btn"
              onClick={() => navigate('/book-consultation')}
              className="group bg-white/5 backdrop-blur-md text-white px-8 py-4 text-lg font-semibold transition-all duration-300 hover:bg-white/10 border border-white/20 hover:border-[#8B5CF6]/50 rounded-xl flex items-center gap-3 w-full sm:w-auto justify-center"
            >
              <Calendar size={20} className="text-[#F97316]" />
              Schedule a Consultation
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-12 mt-16 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <div className="text-center group">
              <div className="text-4xl font-bold bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] bg-clip-text text-transparent group-hover:scale-110 transition-transform">200+</div>
              <div className="text-white/60 text-sm mt-1 font-medium">Projects Delivered</div>
            </div>
            <div className="text-center group">
              <div className="text-4xl font-bold bg-gradient-to-r from-[#8B5CF6] to-[#A78BFA] bg-clip-text text-transparent group-hover:scale-110 transition-transform">100+</div>
              <div className="text-white/60 text-sm mt-1 font-medium">Happy Clients</div>
            </div>
            <div className="text-center group">
              <div className="text-4xl font-bold bg-gradient-to-r from-[#F97316] to-[#FB923C] bg-clip-text text-transparent group-hover:scale-110 transition-transform">99%</div>
              <div className="text-white/60 text-sm mt-1 font-medium">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/20 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-1.5 bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
